import { createSlice, createAsyncThunk } from '@reduxjs/toolkit'
import {
    DocumentInitials,
    CoreDocumentResponseListResponse,
    GetDocumentResponse,
    GetBouncedEmailDocumentResponseList
} from './_documentTypes';
import axios from "axios";
import { handleAxiosError } from '../../axios/handleAxiosError';
import { handleError } from '../../utils/handleError';
import { ODataParams } from '../_ODataTypes';
import { buildApiUrl } from '../../axios/urlCreator';
import { cachedGet } from '../../axios/responseCache';

const initialState: DocumentInitials = {
    documentList: null,
    documentDetail: null,
    bouncedDocumentDetail: null,
    bouncedDocumentList: null,
    loading: false,
    error: null,
};

const documentSlice = createSlice({
    name: 'documents',
    initialState,
    reducers: {

    },
    extraReducers: (builder) => {
        builder
            // GET Document List
            .addCase(getDocumentList.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(getDocumentList.fulfilled, (state, action) => {
                state.loading = false;
                state.error = null;
                state.documentList = action.payload;
            })
            .addCase(getDocumentList.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload as string;
            })
            // GET Document Detail
            .addCase(getDocumentDetail.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(getDocumentDetail.fulfilled, (state, action) => {
                state.loading = false;
                state.error = null;
                state.documentDetail = action.payload;
            })
            .addCase(getDocumentDetail.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload as string;
            })
            // GET Bounced Document Detail
            .addCase(getBouncedDocumentDetail.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(getBouncedDocumentDetail.fulfilled, (state, action) => {
                state.loading = false;
                state.bouncedDocumentDetail = action.payload;
            })
            .addCase(getBouncedDocumentDetail.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload as string;
            })
            // GET Bounced Documents
            .addCase(getBouncedDocuments.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(getBouncedDocuments.fulfilled, (state, action) => {
                state.loading = false;
                state.bouncedDocumentList = action.payload;
            })
            .addCase(getBouncedDocuments.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload as string;
            });
    }
});

// GET Document List
export const getDocumentList = createAsyncThunk<CoreDocumentResponseListResponse, ODataParams | void>(
    'documents/getDocumentList',
    async (params, { rejectWithValue }) => {
        try {
            const queryParams = new URLSearchParams();
            if (params?.filter) queryParams.append('$filter', params.filter);
            if (params?.orderby) queryParams.append('$orderby', params.orderby);
            if (params?.top != null) queryParams.append('$top', params.top.toString());
            if (params?.skip != null) queryParams.append('$skip', params.skip.toString());

            return await cachedGet<CoreDocumentResponseListResponse>(buildApiUrl("documents", queryParams), 15_000);
        }
        catch (error) {
            if (axios.isAxiosError(error)) {
                return rejectWithValue(handleAxiosError(error));
            }
            return rejectWithValue(handleError(error));
        }
    }
);

// GET Document Detail
export const getDocumentDetail = createAsyncThunk<GetDocumentResponse, string>(
    'documents/getDocumentDetail',
    async (documentNumber, { rejectWithValue }) => {
        try {
            return await cachedGet<GetDocumentResponse>(buildApiUrl(`documents/${encodeURIComponent(documentNumber)}`), 15_000);
        }
        catch (error) {
            if (axios.isAxiosError(error)) {
                return rejectWithValue(handleAxiosError(error));
            }
            return rejectWithValue(handleError(error));
        }
    }
);

// GET Bounced Document Detail (by documentNumber)
export const getBouncedDocumentDetail = createAsyncThunk<GetBouncedEmailDocumentResponseList, string>(
    'documents/getBouncedDocumentDetail',
    async (documentNumber, { rejectWithValue }) => {
        try {
            const queryParams = new URLSearchParams({ documentNumber });
            return await cachedGet<GetBouncedEmailDocumentResponseList>(buildApiUrl('documents/bounced', queryParams), 30_000);
        } catch (error) {
            if (axios.isAxiosError(error)) {
                return rejectWithValue(handleAxiosError(error));
            }
            return rejectWithValue(handleError(error));
        }
    }
);

// GET Bounced Documents 
export const getBouncedDocuments = createAsyncThunk<GetBouncedEmailDocumentResponseList>(
    'documents/getBouncedDocuments',
    async (_, { rejectWithValue }) => {
        try {
            return await cachedGet<GetBouncedEmailDocumentResponseList>(buildApiUrl("documents/bounced"), 30_000);
        } catch (error) {
            if (axios.isAxiosError(error)) {
                return rejectWithValue(handleAxiosError(error));
            }
            return rejectWithValue(handleError(error));
        }
    }
);

export default documentSlice.reducer;
